import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { useMemo, useState } from "react";

import { interlude } from "@/interlude";

type RecheckResult = "stillOff" | "unavailable" | null;

const getRecheckResult = ({
  rechecked,
  pending,
  failed,
  active,
}: {
  rechecked: boolean;
  pending: boolean;
  failed: boolean;
  active: boolean | null;
}): RecheckResult => {
  if (!rechecked || pending) return null;
  if (failed || active === null) return "unavailable";
  if (active === false) return "stillOff";
  return null;
};

/**
 * Webhook state of a LINE channel that was just connected, and whether to ask
 * the customer to turn the webhook on.
 *
 * The create response already carries the first answer (`webhookActive`), so
 * nothing is requested on mount. Only "Check again" calls the webhook-check
 * endpoint.
 *
 * `null` means LINE could not be read, which is not evidence the webhook is
 * off, so only an explicit `false` raises the reminder. Once raised, it stays
 * up until LINE says "on" or the customer closes it; a later unreadable answer
 * (null or a failed request) is not a reason to drop it.
 *
 * The state belongs to one connection, so mount the caller once per created
 * channel.
 */
export const useLineWebhookCheck = ({
  channelUuid,
  initialActive,
}: {
  channelUuid: string;
  /** `webhookActive` from the create response. */
  initialActive: boolean | null;
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [rechecked, setRechecked] = useState(false);
  const [active, setActive] = useState(initialActive);

  const checkMutation = interlude.line.useCheckWebhook(
    { params: { channelUuid } },
    {
      onSuccess(result) {
        setActive(result.active);
      },
    },
  );

  const recheck = useHandler(() => {
    setRechecked(true);
    checkMutation.mutate(undefined);
  });

  const dismiss = useHandler(() => {
    setDismissed(true);
  });

  const shouldAsk = initialActive === false && active !== true && !dismissed;
  const rechecking = checkMutation.isPending;
  const recheckResult = getRecheckResult({
    rechecked,
    pending: rechecking,
    failed: checkMutation.isError,
    active,
  });

  return useMemo(
    () => ({ shouldAsk, rechecking, recheckResult, recheck, dismiss }),
    [shouldAsk, rechecking, recheckResult, recheck, dismiss],
  );
};
